interface OpenAIResponse {
  choices: {
    message: {
      content: string;
    };
  }[];
}

interface VariationRequest {
  originalPrompt: string;
  count: number;
  instruction: string;
}

interface VariableVariationRequest {
  originalPrompt: string;
  variableName: string;
  isCreative: boolean;
  count: number;
  context?: {
    aiPlatform?: string;
    domain?: string;
    output?: string;
    platforms?: string[];
    webPlatforms?: string[];
  };
}

interface VariableSuggestionRequest {
  originalPrompt: string;
  variableName: string;
  context?: {
    aiPlatform?: string;
    domain?: string;
    output?: string;
    platforms?: string[];
    webPlatforms?: string[];
  };
}

interface ListingVariationRequest {
  masterTitle: string;
  masterDescription: string;
  variationInstruction: string;
  lockedElements?: string[];
  imageUrl?: string;
}

interface ListingVariationResult {
  title: string;
  description: string;
}

export class OpenAIService {
  private get apiKey(): string {
    // In production, we use Netlify Functions proxy, so no client-side API key needed
    if (import.meta.env.PROD) {
      return 'proxy'; // Placeholder - actual key is server-side only
    }
    
    // For local development, still use the VITE_ prefixed key
    const key = import.meta.env.VITE_OPENAI_API_KEY;
    
    // Enhanced debug logging
    console.log('🔍 OpenAI API Key Debug:', {
      hasKey: !!key,
      keyLength: key?.length || 0,
      keyPreview: key ? `${key.substring(0, 3)}...${key.substring(key.length - 4)}` : 'none',
      startsWithSk: key?.startsWith('sk-') || false,
      environment: import.meta.env.MODE,
      allEnvVars: Object.keys(import.meta.env).filter(k => k.includes('OPENAI')),
      timestamp: new Date().toISOString()
    });
    
    if (!key) {
      console.error('🔴 CRITICAL: OpenAI API key not configured!');
      console.error('🔴 Available env vars:', Object.keys(import.meta.env));
    }
    
    return key || '';
  }
  
  private get baseUrl(): string {
    // Use Netlify Functions proxy in production
    if (import.meta.env.PROD) {
      return '/.netlify/functions';
    }
    // Direct API calls for local development
    return 'https://api.openai.com/v1';
  }

  constructor() {
    // Test API key on initialization
    const testKey = this.apiKey;
    console.log('🚀 OpenAIService initialized with key status:', !!testKey);
  }

  private async makeRequest(endpoint: string, body: any): Promise<OpenAIResponse> {
    const isProduction = import.meta.env.PROD;
    
    if (!isProduction && !this.apiKey) {
      const errorMessage = this.getDetailedApiKeyError();
      console.error('🔴 makeRequest called without API key!', errorMessage);
      throw new Error(errorMessage.userMessage);
    }

    const url = isProduction ? `${this.baseUrl}/openai-chat` : `${this.baseUrl}${endpoint}`;
    
    console.log(`🔵 Making OpenAI request to ${url}:`, {
      endpoint,
      bodyKeys: Object.keys(body),
      isProduction,
      hasApiKey: !isProduction ? !!this.apiKey : 'using-proxy',
      keyPreview: !isProduction && this.apiKey ? `${this.apiKey.substring(0, 3)}...${this.apiKey.substring(this.apiKey.length - 4)}` : 'proxy',
      timestamp: new Date().toISOString()
    });

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Only add Authorization header for local development
      if (!isProduction && this.apiKey) {
        headers['Authorization'] = `Bearer ${this.apiKey}`;
      }

      // Use resilient fetch for SSL error recovery
      const sslRecovery = SSLErrorRecovery.getInstance();
      const response = await sslRecovery.resilientFetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      console.log('OpenAI response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('OpenAI API error:', errorData);
        
        if (response.status === 401) {
          const errorMsg = isProduction 
            ? 'Invalid OpenAI API key on server. Please check OPENAI_API_KEY in Netlify environment variables.'
            : 'Invalid OpenAI API key. Please check your VITE_OPENAI_API_KEY in your .env file and restart the development server.';
          throw new Error(errorMsg);
        } else if (response.status === 429) {
          throw new Error('OpenAI API rate limit exceeded. Please try again in a moment.');
        } else if (response.status === 500) {
          throw new Error('OpenAI API server error. Please try again later.');
        } else {
          throw new Error(`OpenAI API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
        }
      }

      const data: OpenAIResponse = await response.json();
      console.log('OpenAI response data:', data);
      return data;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        // Network or CORS error
        if (!isProduction && (!this.apiKey || this.apiKey.trim() === '')) {
          throw new Error('OpenAI API key is missing. Please add VITE_OPENAI_API_KEY to your .env file and restart the development server.');
        }
        const errorMsg = isProduction
          ? 'Failed to connect to OpenAI proxy. Please check your internet connection and try again.'
          : 'Failed to connect to OpenAI API. Please check your internet connection and try again.';
        throw new Error(errorMsg);
      }
      // Re-throw the original error if it's already a custom error
      throw error;
    }
  }

  private getDetailedApiKeyError(): { userMessage: string; debugInfo: any } {
    const debugInfo = {
      hasApiKey: !!this.apiKey,
      environment: import.meta.env.MODE,
      allViteVars: Object.keys(import.meta.env).filter(key => key.startsWith('VITE_')),
      commonMistakes: Object.entries({
        'OPENAI_API_KEY': !!import.meta.env.OPENAI_API_KEY,
        'NEXT_PUBLIC_OPENAI_API_KEY': !!import.meta.env.NEXT_PUBLIC_OPENAI_API_KEY,
        'REACT_APP_OPENAI_API_KEY': !!import.meta.env.REACT_APP_OPENAI_API_KEY,
      }).filter(([_, exists]) => exists).map(([key]) => key),
      buildTime: import.meta.env.VITE_BUILD_TIME || 'NOT_SET',
      timestamp: new Date().toISOString()
    };

    let userMessage = 'OpenAI API key not configured. ';

    if (import.meta.env.MODE === 'development') {
      userMessage += 'Add VITE_OPENAI_API_KEY to your .env file and restart the dev server.';
    } else {
      userMessage += 'To fix this:\n\n1. Go to Netlify Dashboard → Site Settings → Environment Variables\n2. Add: VITE_OPENAI_API_KEY = your-openai-api-key\n3. Trigger a new deployment\n4. Ensure your key starts with "sk-"\n\nNote: Environment variables must be set BEFORE deployment in Netlify.';
    }

    // Add specific guidance if common mistakes are found
    if (debugInfo.commonMistakes.length > 0) {
      userMessage += `\n\nFound incorrect variable names: ${debugInfo.commonMistakes.join(', ')}. For Vite apps, use VITE_OPENAI_API_KEY instead.`;
    }

    return { userMessage, debugInfo };
  }

  async generateListingVariation({
    masterTitle,
    masterDescription,
    variationInstruction,
    lockedElements = [],
    imageUrl
  }: ListingVariationRequest): Promise<ListingVariationResult> {
    const lockedElementsText = lockedElements.length > 0 
      ? `\n\nIMPORTANT - Keep these elements EXACTLY the same:\n${lockedElements.map(el => `- ${el}`).join('\n')}`
      : '';

    const imageContext = imageUrl 
      ? `\n\nIMPORTANT - A design image has been provided. Analyze the visual elements, colors, style, and features shown in the image to create more accurate and contextually relevant variations. The image shows the actual product being listed.`
      : '';
    const systemPrompt = `You are an expert e-commerce listing optimizer and copywriter. Your task is to create a single variation of a product listing based on specific user instructions.

CRITICAL REQUIREMENTS:
1. Generate exactly ONE complete listing variation
2. The variation must include both a TITLE and DESCRIPTION
3. Follow the user's variation instruction precisely
4. Maintain the core product value while applying the requested changes
5. Keep the title concise and compelling (under 80 characters when possible)
6. Make the description detailed and persuasive while following the instruction${lockedElementsText}${imageContext}

OUTPUT FORMAT:
Return the variation in this exact format:
TITLE: [Generated title]
DESCRIPTION: [Generated description]

IMPORTANT: 
- Do not include numbering, bullet points, or extra formatting
- Start the title with "TITLE:" and the description with "DESCRIPTION:"
- Ensure the variation follows the user's instruction while maintaining product appeal
- If an image is provided, incorporate visual details you can observe to make the listing more accurate and appealing`;

    const userPrompt = `Master Listing:
TITLE: ${masterTitle}
DESCRIPTION: ${masterDescription}

Variation Instruction: ${variationInstruction}

Generate a single variation that follows this instruction. The variation should have both a title and description that work together as a complete listing.`;

    try {
      // Determine the model to use based on whether we have an image
      const model = imageUrl ? 'gpt-4o' : 'gpt-4o-mini';
      
      // Build the messages array
      const messages = [
        {
          role: 'system',
          content: systemPrompt
        },
        {
          role: 'user',
          content: imageUrl ? [
            {
              type: 'text',
              text: userPrompt
            },
            {
              type: 'image_url',
              image_url: {
                url: imageUrl,
                detail: 'high'
              }
            }
          ] : userPrompt
        }
      ];

      const data = await this.makeRequest('/chat/completions', {
        model,
        messages,
        max_tokens: 800,
        temperature: 0.7,
        top_p: 0.9,
        presence_penalty: 0.2,
        frequency_penalty: 0.1,
      });

      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error('No content received from OpenAI API');
      }

      console.log('Raw OpenAI response:', content);

      // Parse the response to extract title and description
      const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      
      let title = '';
      let description = '';
      
      for (const line of lines) {
        if (line.startsWith('TITLE:')) {
          title = line.replace('TITLE:', '').trim();
        } else if (line.startsWith('DESCRIPTION:')) {
          description = line.replace('DESCRIPTION:', '').trim();
        } else if (description) {
          // Continue description on next line
          description += ' ' + line;
        }
      }

      console.log('Parsed variation:', { title, description });

      if (!title || !description) {
        throw new Error('Could not parse title and description from the response');
      }

      return { title, description };
    } catch (error) {
      console.error('Error generating listing variation:', error);
      throw error;
    }
  }

  async generateTitle(promptBody: string): Promise<string> {
    const systemPrompt = `You are an AI assistant that generates clear, searchable titles for AI-generated video prompts. Focus on describing the main visual or concept in 3–6 words. Avoid poetic or abstract language. The title should be literal, clean, and discoverable—like what someone might type into a search bar to find this video. Format: title only, no punctuation.`;

    const userPrompt = `Create a concise, descriptive title for this AI prompt:

"${promptBody}"

The title should clearly indicate what this prompt is designed to do or create.`;

    try {
      const data = await this.makeRequest('/chat/completions', {
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userPrompt
          }
        ],
        max_tokens: 50,
        temperature: 0.7,
        top_p: 0.9,
      });

      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error('No content received from OpenAI API');
      }

      // Clean up the title - remove quotes if present and trim
      const title = content.trim().replace(/^["']|["']$/g, '');
      
      return title;
    } catch (error) {
      console.error('Error generating title:', error);
      throw error;
    }
  }

  private buildContextualPrompt(
    originalPrompt: string, 
    variableName: string, 
    context?: {
      aiPlatform?: string;
      domain?: string;
      output?: string;
      platforms?: string[];
      webPlatforms?: string[];
    }
  ): string {
    let contextInfo = '';
    
    if (context) {
      const contextParts = [];
      
      if (context.aiPlatform) {
        contextParts.push(`AI Platform: ${context.aiPlatform}`);
      }
      
      if (context.domain) {
        contextParts.push(`Domain/Industry: ${context.domain}`);
      }
      
      if (context.output) {
        contextParts.push(`Expected Output: ${context.output}`);
      }
      
      if (context.platforms && context.platforms.length > 0) {
        contextParts.push(`AI Platforms: ${context.platforms.join(', ')}`);
      }
      
      if (context.webPlatforms && context.webPlatforms.length > 0) {
        contextParts.push(`Web Platforms/Marketplaces: ${context.webPlatforms.join(', ')}`);
      }
      
      if (contextParts.length > 0) {
        contextInfo = `\n\nAdditional Context:\n${contextParts.join('\n')}`;
      }
    }
    
    return `Prompt: "${originalPrompt}"${contextInfo}\n\nVariable to suggest for: [${variableName}]`;
  }

  async suggestVariableInputs(
    originalPromptBody: string, 
    variableName: string, 
    context?: {
      aiPlatform?: string;
      domain?: string;
      output?: string;
      platforms?: string[];
      webPlatforms?: string[];
    }
  ): Promise<string[]> {
    const systemPrompt = `You are an expert prompt engineer and creative assistant with deep knowledge of AI platforms, industries, and content creation. Your task is to suggest highly relevant, contextual inputs for template variables.

CRITICAL REQUIREMENTS:
1. Return CONCISE values only (1-3 words maximum per suggestion)
2. Match the variable's expected format and scope
3. NO descriptive sentences, explanations, or additional details
4. Just the core value that would fit in the variable slot
5. Think "what would I type in this field?" not "how would I describe this?"
6. Analyze ALL provided context (AI platform, domain, output type, web platforms)
7. Consider the specific AI platform's strengths and typical use cases
8. Factor in the target domain/industry requirements
9. Align suggestions with the expected output type
10. Consider the web platform/marketplace where content will be used
11. Make suggestions that would actually work well together in the real world
12. Avoid generic suggestions - be specific and actionable
13. Ensure variety while maintaining relevance

EXAMPLES:
- Variable [type of stone] → "rough opal", "polished granite", "raw emerald" (NOT "Rough opal with iridescent effects")
- Variable [style] → "vintage", "modern", "minimalist" (NOT "A vintage-inspired design with classic elements")
- Variable [color] → "deep blue", "forest green", "sunset orange" (NOT "A rich deep blue that evokes ocean depths")

CONTEXT ANALYSIS PRIORITIES:
- If AI Platform is specified (ChatGPT, Claude, Midjourney, etc.), tailor suggestions to that platform's capabilities
- If Domain is specified (Marketing, E-commerce, etc.), focus on industry-specific terminology
- If Output is specified (product descriptions, images, etc.), ensure suggestions fit that format
- If Web Platforms are specified (Etsy, YouTube, etc.), consider platform-specific requirements

Return exactly 5 diverse but contextually relevant suggestions, one per line, without numbering. Each suggestion must be 1-3 words maximum.`;

    const contextualPrompt = this.buildContextualPrompt(originalPromptBody, variableName, context);

    const userPrompt = `${contextualPrompt}

Based on the prompt content AND all provided context (AI platform, domain, output type, web platforms), suggest 5 highly relevant and specific inputs for the variable [${variableName}].

IMPORTANT: 
- Use the context to make suggestions that are specifically tailored to the use case
- Consider how this variable will be used in the real world on the specified platforms
- Make suggestions that would actually work well together with the other context
- Be specific rather than generic
- Ensure suggestions are practical and actionable

Focus on suggestions that someone in this specific domain, using this AI platform, creating this type of output, for these web platforms would actually find useful and relevant.`;

    try {
      const data = await this.makeRequest('/chat/completions', {
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userPrompt
          }
        ],
        max_tokens: 300,
        temperature: 0.5, // Reduced further for more focused, concise suggestions
        top_p: 0.8, // Reduced for more consistent quality
        presence_penalty: 0.3, // Encourage variety
        frequency_penalty: 0.2, // Reduce repetition
      });

      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error('No content received from OpenAI API');
      }

      // Parse the response into individual suggestions
      const suggestions = content
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => {
          // Remove numbering if present
          return line.replace(/^\d+[\.\)]\s*/, '').replace(/^[•\-\*]\s*/, '').trim();
        })
        .filter(line => line.length > 0)
        .slice(0, 5); // Ensure we only return 5 suggestions

      return suggestions;
    } catch (error) {
      console.error('Error generating variable suggestions:', error);
      throw error;
    }
  }

  async generateVariableVariations({ 
    originalPrompt, 
    variableName, 
    isCreative, 
    count, 
    context 
  }: VariableVariationRequest): Promise<string[]> {
    
    const systemPrompt = isCreative 
      ? `You are a creative AI assistant specializing in generating unique, innovative variations for prompt variables. You have deep expertise in AI platforms, content creation, and industry-specific requirements.

CREATIVE GENERATION REQUIREMENTS:
1. Analyze ALL provided context (AI platform, domain, output, web platforms)
2. Generate ${count} creative but concise variations (1-3 words each)
3. Think outside conventional boundaries while remaining useful
4. Consider cross-industry inspiration and unexpected combinations
5. Ensure suggestions would actually enhance the prompt's effectiveness
6. Balance creativity with real-world applicability
7. Avoid clichés and overused terms
8. Keep each suggestion SHORT - just the creative concept, not a full description

CONTEXT-DRIVEN CREATIVITY:
- Use the AI platform's unique capabilities for inspiration
- Draw from the target domain's cutting-edge trends
- Consider emerging styles and approaches in the output type
- Think about what would stand out on the specified web platforms

CREATIVE TECHNIQUES:
- Combine concepts from different industries
- Use unexpected adjectives and descriptors
- Reference current trends and cultural moments
- Apply artistic and design principles
- Consider sensory and emotional elements
- IMPORTANT: Return only short, creative values (1-3 words), NOT full sentences or descriptions

Return exactly ${count} creative variations, one per line, without numbering. Each should be 1-3 words maximum.`
      : `You are an expert prompt engineer with comprehensive knowledge of AI platforms, industries, and content creation best practices. Your task is to generate ${count} highly contextual variations for the variable [${variableName}].

CONTEXTUAL GENERATION REQUIREMENTS:
1. Deeply analyze ALL provided context (AI platform, domain, output, web platforms)
2. Generate variations that are perfectly suited to the specific use case
3. Consider platform-specific requirements and limitations
4. Factor in industry standards and best practices
5. Ensure variations would work seamlessly together
6. Focus on proven, effective approaches
7. Maintain consistency with the overall prompt purpose

CONTEXT ANALYSIS:
- AI Platform capabilities and typical workflows
- Domain/industry terminology and requirements
- Output format specifications and constraints
- Web platform audience and content standards

Return exactly ${count} contextual variations, one per line, without numbering.`;

    const contextualPrompt = this.buildContextualPrompt(originalPrompt, variableName, context);

    const userPrompt = `${contextualPrompt}

Generate ${count} ${isCreative ? 'creative but practical' : 'highly contextual'} variations for the variable [${variableName}].

${isCreative ? 
  `CREATIVE BRIEF: Think innovatively while considering the context. Generate variations that would:
  - Be creative but concise (1-3 words each)
  - Stand out from typical approaches
  - Be immediately usable in the prompt context
  - Leverage unique aspects of the specified AI platform
  
  CRITICAL: Each suggestion must be SHORT (1-3 words) - just the creative concept, not a description or sentence.
  
  Examples of GOOD creative suggestions for [object]: "crystalline orb", "neo-baroque", "biomimetic"
  Examples of BAD suggestions: "A velvety layer of...", "An ornate design featuring..."` : 
  `CONTEXTUAL BRIEF: Generate variations that would:
  - Work perfectly with the specified AI platform
  - Meet the target domain's professional standards
  - Produce excellent results for the specified output type
  - Perform optimally on the target web platforms
  - Be immediately useful to practitioners in this field
  
  Focus on proven, effective approaches that professionals in this context would actually use.`
}

Consider the variable name "${variableName}" and ensure all suggestions make logical sense for this variable in the given context.`;

    try {
      const data = await this.makeRequest('/chat/completions', {
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userPrompt
          }
        ],
        max_tokens: 400,
        temperature: isCreative ? 0.8 : 0.6,
        top_p: isCreative ? 0.85 : 0.8,
        presence_penalty: 0.4, // Encourage more variety
        frequency_penalty: 0.3, // Reduce repetition significantly
      });

      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error('No content received from OpenAI API');
      }

      // Parse the response into individual variations
      const variations = content
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => {
          // Remove numbering if present
          return line.replace(/^\d+[\.\)]\s*/, '').replace(/^[•\-\*]\s*/, '').trim();
        })
        .filter(line => line.length > 0)
        .slice(0, count); // Ensure we only return requested count

      return variations;
    } catch (error) {
      console.error('Error generating variable variations:', error);
      throw error;
    }
  }

  async generateCohesiveVariableSet(
    templatePrompt: string,
    variables: string[],
    context?: {
      aiPlatform?: string;
      domain?: string;
      output?: string;
      platforms?: string[];
      webPlatforms?: string[];
    }
  ): Promise<Record<string, string>> {
    const contextInfo = this.buildContextInfo(context);
    
    const systemPrompt = `You are an expert prompt engineer and creative director with deep knowledge of thematic coherence and contextual consistency. Your task is to generate a cohesive set of variable values that work harmoniously together.

CRITICAL REQUIREMENTS:
1. Generate values that create a unified, coherent theme
2. Ensure all variables complement each other perfectly
3. Consider the overall aesthetic, style, and purpose
4. Make values that would realistically appear together
5. Avoid mismatched or conflicting combinations
6. Keep each value concise (1-3 words typically)
7. Consider the context and intended use case

THEMATIC COHERENCE PRINCIPLES:
- Style consistency (if one variable is "vintage", others should complement that era/aesthetic)
- Color harmony (colors that work well together)
- Conceptual alignment (elements that belong in the same world/scenario)
- Scale appropriateness (elements that make sense at the same scale)
- Cultural/contextual consistency (elements from compatible contexts)

OUTPUT FORMAT:
Return exactly one value per variable in this format:
[VARIABLE_NAME]: suggested_value

Example:
[style]: vintage
[color]: sepia brown
[material]: aged leather
[effect]: nostalgic glow

IMPORTANT: Return ONLY the variable assignments, nothing else. Each variable should get exactly one cohesive value that works perfectly with all others.`;

    const userPrompt = `Template: "${templatePrompt}"

Variables to generate cohesive values for:
${variables.map(v => `[${v}]`).join(', ')}

${contextInfo}

Generate a cohesive, thematically consistent set of values where all variables work harmoniously together. Consider what theme or aesthetic would make these elements feel like they naturally belong in the same creative work.

Focus on creating a unified vision where someone looking at the final result would think "these elements were clearly designed to work together."`;

    try {
      const data = await this.makeRequest('/chat/completions', {
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userPrompt
          }
        ],
        max_tokens: 400,
        temperature: 0.7,
        top_p: 0.85,
        presence_penalty: 0.2,
        frequency_penalty: 0.1,
      });

      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error('No content received from OpenAI API');
      }

      console.log('Raw cohesive suggestions response:', content);

      // Parse the response into a key-value object
      const suggestions: Record<string, string> = {};
      const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      
      for (const line of lines) {
        const match = line.match(/\[([^\]]+)\]:\s*(.+)/);
        if (match) {
          const [, variableName, value] = match;
          suggestions[variableName] = value.trim();
        }
      }

      console.log('Parsed cohesive suggestions:', suggestions);

      // Ensure we have suggestions for all requested variables
      const missingSuggestions = variables.filter(v => !suggestions[v]);
      if (missingSuggestions.length > 0) {
        console.warn('Missing suggestions for variables:', missingSuggestions);
      }

      return suggestions;
    } catch (error) {
      console.error('Error generating cohesive variable set:', error);
      throw error;
    }
  }

  private buildContextInfo(context?: {
    aiPlatform?: string;
    domain?: string;
    output?: string;
    platforms?: string[];
    webPlatforms?: string[];
  }): string {
    if (!context) return '';
    
    const contextParts = [];
    
    if (context.aiPlatform) {
      contextParts.push(`AI Platform: ${context.aiPlatform}`);
    }
    
    if (context.domain) {
      contextParts.push(`Domain/Industry: ${context.domain}`);
    }
    
    if (context.output) {
      contextParts.push(`Expected Output: ${context.output}`);
    }
    
    if (context.platforms && context.platforms.length > 0) {
      contextParts.push(`AI Platforms: ${context.platforms.join(', ')}`);
    }
    
    if (context.webPlatforms && context.webPlatforms.length > 0) {
      contextParts.push(`Web Platforms/Marketplaces: ${context.webPlatforms.join(', ')}`);
    }
    
    if (contextParts.length > 0) {
      return `\n\nAdditional Context:\n${contextParts.join('\n')}`;
    }
    
    return '';
  }

  async generateVariations({ originalPrompt, count, instruction }: VariationRequest): Promise<string[]> {
    const systemPrompt = `You are an expert prompt engineer. Your task is to generate ${count} distinct variations of the given prompt based on the user's specific instructions. Each variation should:

1. Maintain the core purpose and intent of the original prompt
2. Be significantly different from the original and other variations
3. Incorporate the user's specific requirements and modifications
4. Be ready to use as-is
5. Be complete and well-structured

Focus on creating meaningful variations that truly reflect the user's requested changes while preserving what makes the original prompt effective.

IMPORTANT: Return ONLY the variations, one per line, without any numbering, bullet points, or additional text. Each line should be a complete, ready-to-use prompt variation.`;

    const userPrompt = `Original prompt: "${originalPrompt}"

User's variation instructions: "${instruction}"

Generate ${count} distinct variations that incorporate the user's instructions. Each variation should be meaningfully different while maintaining the original prompt's core purpose.`;

    try {
      const data = await this.makeRequest('/chat/completions', {
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userPrompt
          }
        ],
        max_tokens: 1500,
        temperature: 0.8,
        top_p: 0.9,
        presence_penalty: 0.3,
        frequency_penalty: 0.2,
      });

      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error('No content received from OpenAI API');
      }

      console.log('Raw OpenAI response:', content);

      // Parse the response - handle both numbered and non-numbered formats
      const variations = content
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => {
          // Remove numbering if present (e.g., "1. ", "1) ", "• ", etc.)
          return line.replace(/^\d+[\.\)]\s*/, '').replace(/^[•\-\*]\s*/, '').trim();
        })
        .filter(line => line.length > 0)
        .slice(0, count); // Ensure we don't exceed requested count

      console.log('Parsed variations:', variations);

      if (variations.length === 0) {
        throw new Error('No valid variations could be parsed from the response');
      }

      return variations;
    } catch (error) {
      console.error('Error generating variations:', error);
      throw error;
    }
  }

  async enhancePrompt(originalPrompt: string): Promise<string> {
    const systemPrompt = `You are an expert prompt engineer. Your task is to enhance and optimize prompts for AI systems. Take the given prompt and improve it by:

1. Making it clearer and more specific
2. Adding relevant context and details
3. Structuring it for better AI understanding
4. Maintaining the original intent while making it more effective

CRITICAL VARIABLE PRESERVATION RULES:
- If the prompt contains variables in [brackets], you MUST preserve them exactly
- Variables can be duplicated or repositioned for better flow
- DO NOT remove, rename, or replace any existing variables
- DO NOT add new variables - only use the ones already present
- Keep the exact variable names like [style], [color], [object] unchanged

Return only the enhanced prompt, nothing else.`;

    try {
      const data = await this.makeRequest('/chat/completions', {
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: `Please enhance this prompt: "${originalPrompt}"`
          }
        ],
        max_tokens: 800,
        temperature: 0.7,
      });

      return data.choices[0]?.message?.content?.trim() || originalPrompt;
    } catch (error) {
      console.error('Error enhancing prompt:', error);
      throw error;
    }
  }
}

import { SSLErrorRecovery } from '../utils/siteHealth';
export const openAIService = new OpenAIService();